import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { CreditCard, CheckCircle2, Clock, XCircle, Wine, ShoppingBag } from "lucide-react";

const PAY_STATUS = {
    paid: { label: "Paga", color: "text-vineyard bg-vineyard/10", icon: CheckCircle2 },
    pending: { label: "Pendente", color: "text-gold-deep bg-gold-soft/20", icon: Clock },
    unpaid: { label: "Não paga", color: "text-destructive bg-destructive/10", icon: XCircle },
    expired: { label: "Expirada", color: "text-wine-dark/60 bg-wine-dark/5", icon: XCircle },
};

export default function AdminPayments() {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        api.get("/admin/payments").then((r) => setData(r.data));
    }, []);

    if (!data) return <p className="text-wine-dark/60 label-overline">A carregar…</p>;

    const txs = filter === "all" ? data.transactions : data.transactions.filter((t) => t.payment_status === filter);

    return (
        <section className="space-y-6" data-testid="admin-payments">
            <div>
                <span className="label-overline text-wine/60">Stripe</span>
                <h1 className="font-heading text-3xl text-wine-dark">Pagamentos</h1>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi label="Total transações" value={data.totals.count} icon={CreditCard} />
                <Kpi label="Pagas" value={data.totals.paid_count} icon={CheckCircle2} accent />
                <Kpi label="Pendentes" value={data.totals.pending_count} icon={Clock} />
                <Kpi label="Receita confirmada" value={`€${data.totals.paid_amount.toFixed(2)}`} icon={ShoppingBag} />
            </div>

            <div className="bg-white rounded-md border border-gold-soft/30 p-5">
                <div className="flex gap-1 bg-cream rounded-md p-1 w-fit mb-4">
                    {["all", "paid", "pending", "unpaid"].map((f) => (
                        <button key={f} onClick={() => setFilter(f)} className={`px-4 py-1.5 rounded-md text-xs uppercase tracking-wider ${filter === f ? "bg-wine text-cream" : "text-wine-dark/60"}`} data-testid={`pay-filter-${f}`}>
                            {f === "all" ? "Todas" : PAY_STATUS[f]?.label}
                        </button>
                    ))}
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left label-overline text-wine-dark/60 border-b border-wine-dark/10">
                                <th className="py-3 px-3">Sessão</th><th>Cliente</th><th>Tipo</th><th>Valor</th><th>Estado</th><th>Data</th>
                            </tr>
                        </thead>
                        <tbody>
                            {txs.map((t) => {
                                const st = PAY_STATUS[t.payment_status] || PAY_STATUS.pending;
                                const Icon = st.icon;
                                return (
                                    <tr key={t.session_id} className="border-t border-wine-dark/5 hover:bg-cream" data-testid={`payment-row`}>
                                        <td className="py-3 px-3 font-mono text-[11px] text-wine-dark/70">{t.session_id?.slice(-14) || "—"}</td>
                                        <td className="text-wine-dark text-xs">{t.user || <span className="text-wine-dark/40">—</span>}</td>
                                        <td>
                                            <span className="inline-flex items-center gap-1.5 text-xs text-wine-dark/70">
                                                {t.kind === "subscription" ? <Wine className="h-3.5 w-3.5" strokeWidth={1.5} /> : <ShoppingBag className="h-3.5 w-3.5" strokeWidth={1.5} />}
                                                {t.kind === "subscription" ? "Clube" : "Encomenda"}
                                            </span>
                                        </td>
                                        <td className="font-heading text-base text-wine-dark">€{(t.amount || 0).toFixed(2)}</td>
                                        <td>
                                            <span className={`inline-flex items-center gap-1 label-overline px-2 py-1 rounded text-[10px] ${st.color}`}>
                                                <Icon className="h-3 w-3" /> {st.label}
                                            </span>
                                        </td>
                                        <td className="text-xs text-wine-dark/55">{t.created_at ? new Date(t.created_at).toLocaleDateString("pt-PT") : "—"}</td>
                                    </tr>
                                );
                            })}
                            {txs.length === 0 && (
                                <tr><td colSpan="6" className="text-center py-10 text-wine-dark/60">Sem transações nesta categoria.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}

function Kpi({ label, value, icon: Icon, accent }) {
    return (
        <div className={`p-5 rounded-md border shadow-sm ${accent ? "bg-wine-dark text-cream border-wine-dark" : "bg-white border-gold-soft/30"}`}>
            <div className="flex items-start justify-between">
                <span className={`label-overline ${accent ? "text-gold-soft" : "text-wine/60"}`}>{label}</span>
                <Icon className={`h-4 w-4 ${accent ? "text-gold-soft" : "text-wine/40"}`} strokeWidth={1.5} />
            </div>
            <p className="font-heading text-3xl mt-2">{value}</p>
        </div>
    );
}
